import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { PatronaVenster } from "../../pages/admin/PatronaPage";
import { useAdminAuth } from "../../lib/adminAuth";
import { adminGet } from "../../lib/adminApi";
import { Badge, Button, Input, MeasureRule } from "../fulkra";

/**
 * De vaste omlijsting van het beheerdeel, in de huisstijl.
 *
 * Restyling, geen herbouw: de menu-items, de rolfilter, het tellertje bij pijn-signalen
 * dat elke 30 seconden ververst, de mobiele schuiflade en alle data-testid's zijn
 * ongewijzigd. Alleen de vormgeving is anders.
 *
 * De zijbalk staat op inkt — dat donkere vlak is het merkanker (STIJL.md), met de
 * schaallat eronder als meetmotief. Per scherm is er één donker vlak, dus de kopbalk en
 * de werkruimte blijven licht.
 */

const NAV_ITEMS = [
  { to: "/admin", label: "Dashboard", end: true, testid: "nav-dashboard", roles: ["admin", "aanmeter", "podotherapeut"] },
  { to: "/admin/werkgevers", label: "Werkgevers", testid: "nav-werkgevers", roles: ["admin"] },
  { to: "/admin/werknemers", label: "Werknemers", testid: "nav-werknemers", roles: ["admin", "podotherapeut"] },
  { to: "/admin/aanmeetdagen", label: "Aanmeetdagen", testid: "nav-aanmeetdagen", roles: ["admin", "aanmeter"] },
  { to: "/admin/bestellingen", label: "Bestellingen", testid: "nav-bestellingen", roles: ["admin"] },
  { to: "/admin/pijn-flags", label: "Pijn-signalen", testid: "nav-pijnflags", badge: "pijn", roles: ["admin", "podotherapeut"] },
  { to: "/admin/notificaties", label: "Notificaties", testid: "nav-notificaties", roles: ["admin"] },
  { to: "/admin/email-templates", label: "Email-templates", testid: "nav-emailtemplates", roles: ["admin"] },
  { to: "/admin/gebruikers", label: "Gebruikers", testid: "nav-gebruikers", roles: ["admin"] },
  { to: "/admin/audit-log", label: "Audit-log", testid: "nav-auditlog", roles: ["admin"] },
  { to: "/admin/instellingen", label: "Instellingen", testid: "nav-instellingen", roles: ["admin"] },
];

function userHasAnyRole(user, roles) {
  if (!user || !roles || roles.length === 0) return true;
  const userRollen = user.rollen && user.rollen.length > 0 ? user.rollen : (user.rol ? [user.rol] : []);
  return roles.some((r) => userRollen.includes(r));
}

const ZIJBALK_TEKST = "rgba(247, 249, 250, .72)";
const ZIJBALK_ZACHT = "rgba(247, 249, 250, .5)";

/* Vanaf tabletbreedte staat de zijbalk vast en schuift hij niet meer. Dat kan niet met
   een inline-stijl: media-regels bestaan daar niet. */
const MEDIA_REGELS = [
  "@media (min-width: 768px) {",
  "  .fulkra-zijbalk { position: sticky !important; transform: none !important; height: 100vh; }",
  "  .fulkra-menuknop { display: none !important; }",
  "}",
].join("\n");

export function AdminLayout() {
  const { user, logout } = useAdminAuth();
  const navigate = useNavigate();
  const locatie = useLocation();
  const [patronaOpen, setPatronaOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [zoekterm, setZoekterm] = useState("");
  const [pijnOpen, setPijnOpen] = useState(0);

  // Polling pijn-flag open-count elke 30s, pauze bij document.hidden
  useEffect(() => {
    let timer = null;
    let cancelled = false;

    async function fetchPijnCount() {
      if (document.hidden) return;
      try {
        const data = await adminGet("/api/admin/pijn-flags", { afgehandeld: false, limit: 1 });
        if (!cancelled) setPijnOpen(data?.total ?? data?.items?.length ?? 0);
      } catch {
        // best-effort
      }
    }

    fetchPijnCount();
    timer = setInterval(fetchPijnCount, 30000);
    const onVis = () => { if (!document.hidden) fetchPijnCount(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  async function handleLogout() {
    await logout();
    navigate("/admin/login", { replace: true });
  }

  const zichtbareItems = NAV_ITEMS.filter((item) => userHasAnyRole(user, item.roles));

  return (
    <div
      className="fulkra"
      style={{ display: "flex", minHeight: "100vh", background: "var(--surface-page)" }}
      data-testid="admin-layout"
    >
      <aside
        data-testid="admin-sidebar"
        className="fulkra-zijbalk"
        style={{
          position: "fixed", top: 0, bottom: 0, left: 0, zIndex: 30,
          width: "var(--sidebar-breedte)", flexShrink: 0,
          display: "flex", flexDirection: "column",
          background: "var(--ink)",
          transform: mobileOpen ? "translateX(0)" : "translateX(-100%)",
          transition: "transform var(--duur-normaal) var(--ease-standaard)",
        }}
      >
        <div
          style={{
            height: "var(--topbar-hoogte)", display: "flex", alignItems: "center", gap: 6,
            padding: "0 var(--ruimte-6)", borderBottom: "1px solid rgba(247, 249, 250, .12)",
          }}
        >
          <Link
            to="/admin"
            style={{
              fontFamily: "var(--font-kop)", fontWeight: "var(--fw-semi)", fontSize: "var(--fs-body-lg)",
              letterSpacing: "var(--ls-kop)", color: "var(--text-inverse)", textDecoration: "none",
            }}
          >
            Fulkra Works
          </Link>
          <span style={{ fontSize: "var(--fs-sm)", color: ZIJBALK_ZACHT }}>Admin</span>
        </div>

        <MeasureRule height={8} spacing={7} accent="rgba(247, 249, 250, .18)" />

        <nav style={{ display: "flex", flexDirection: "column", gap: 2, padding: "var(--ruimte-4)" }}>
          {zichtbareItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              onClick={() => setMobileOpen(false)}
              style={({ isActive }) => ({
                display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: "var(--ruimte-4)", minHeight: 36,
                padding: "var(--ruimte-3) var(--ruimte-5)", borderRadius: "var(--radius)",
                textDecoration: "none", fontSize: "var(--fs-body)", lineHeight: "var(--lh-tekst)",
                fontWeight: isActive ? "var(--fw-medium)" : "var(--fw-normaal)",
                background: isActive ? "var(--actie)" : "transparent",
                color: isActive ? "#fff" : ZIJBALK_TEKST,
                transition: "var(--overgang-kleur)",
              })}
            >
              {({ isActive }) => (
                <>
                  <span>{item.label}</span>
                  {item.badge === "pijn" && pijnOpen > 0 ? (
                    <Badge tone={isActive ? "inkt" : "alarm"} pill data-testid="sidebar-pijn-badge">{pijnOpen}</Badge>
                  ) : null}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div
          style={{
            marginTop: "auto", padding: "var(--ruimte-6)",
            fontSize: "var(--fs-xs)", color: ZIJBALK_ZACHT,
            borderTop: "1px solid rgba(247, 249, 250, .12)",
          }}
        >
          {user?.naam || user?.email}
          {user?.rol ? " · " + user.rol : ""}
        </div>
      </aside>

      {mobileOpen ? (
        <button
          aria-label="Sluit menu"
          onClick={() => setMobileOpen(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 20,
            background: "var(--overlay)", border: "none", cursor: "pointer",
          }}
        />
      ) : null}

      <div style={{ display: "flex", minHeight: "100vh", flex: 1, flexDirection: "column", minWidth: 0 }}>
        <header
          style={{
            position: "sticky", top: 0, zIndex: 10,
            height: "var(--topbar-hoogte)", display: "flex", alignItems: "center",
            justifyContent: "space-between", gap: "var(--ruimte-6)",
            padding: "0 var(--ruimte-8)",
            background: "var(--wit)", borderBottom: "1px solid var(--border-subtle)",
          }}
        >
          <button
            type="button"
            aria-label="Menu openen"
            data-testid="topbar-mobile-toggle"
            onClick={() => setMobileOpen(true)}
            className="fulkra-menuknop"
            style={{
              minHeight: 36, minWidth: 36, borderRadius: "var(--radius)",
              border: "1px solid var(--border-subtle)", background: "var(--wit)",
              color: "var(--text-body)", cursor: "pointer", fontSize: "var(--fs-body)",
            }}
          >
            {"☰"}
          </button>

          {/* Zoeken doet wat het zegt: het brengt je naar de werknemerslijst met deze
              zoekterm erin. Dat is dezelfde zoekfunctie die die pagina zelf gebruikt. */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const term = zoekterm.trim();
              navigate(term ? `/admin/werknemers?q=${encodeURIComponent(term)}` : "/admin/werknemers");
            }}
            style={{ flex: "0 1 280px", minWidth: 0 }}
            role="search"
          >
            <Input
              type="search"
              mobiel={false}
              value={zoekterm}
              onChange={(e) => setZoekterm(e.target.value)}
              placeholder="Zoek werknemer of klantnummer"
              aria-label="Zoek werknemer of klantnummer"
              data-testid="topbar-zoek"
              style={{ height: "var(--veld-hoogte-desktop)", minHeight: "var(--veld-hoogte-desktop)" }}
            />
          </form>

          {/* Het meetmotief loopt door in de kopbalk, zoals het ontwerp het tekent. */}
          <MeasureRule height={10} spacing={7} style={{ flex: "1 1 auto", maxWidth: 220 }} />

          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "var(--ruimte-6)" }}>
            <div style={{ textAlign: "right" }}>
              <div
                data-testid="topbar-username"
                style={{ fontSize: "var(--fs-xs)", fontWeight: "var(--fw-medium)", color: "var(--text-body)" }}
              >
                {user?.naam || user?.email}
              </div>
              <div data-testid="topbar-role" style={{ fontSize: "var(--fs-xs)", color: "var(--text-soft)" }}>
                {user?.rol}
              </div>
            </div>
            <Button variant="secundair" size="sm" onClick={handleLogout} data-testid="btn-logout">
              Uitloggen
            </Button>
          </div>
        </header>

        <main style={{ flex: 1, padding: "var(--ruimte-8)" }} data-testid="admin-main">
          <div style={{ margin: "0 auto", width: "100%", maxWidth: "var(--admin-max)" }}>
            <Outlet />
          </div>
        </main>

        {/* Patrona is vanaf elk beheerscherm één tik weg. Niet op de pagina zelf: daar
            zou de knop bovenop het chatvenster staan en nergens heen leiden. De opmaak
            staat in styles/patrona.css, inclusief de hogere plek op een smal scherm zodat
            hij geen knop onderaan een formulier afdekt.

            Alleen voor beheerders, net als de menu-items hierboven: het eindpunt eist
            require_role("admin"). Zonder deze filter zag een aanmeter de knop, typte een
            vraag met bijlagen, en kreeg pas dán een 403 in techniek-taal. */}
        {userHasAnyRole(user, ["admin"]) && !locatie.pathname.startsWith("/admin/patrona") && !patronaOpen ? (
          <button
            type="button"
            className="patrona-zweefknop"
            onClick={() => setPatronaOpen(true)}
            aria-label="Patrona openen"
            title="Patrona openen"
            data-testid="btn-patrona-openen"
          >
            <MessageCircle size={24} aria-hidden="true" />
          </button>
        ) : null}
      </div>

      {/* Het venster hangt hier, buiten de werkruimte: daarbinnen heeft het een maximale
          breedte en vaste marges, en dan wordt het gesprek afgekapt. Zo komt het over het
          scherm dat je al open had, en sta je na het sluiten weer waar je was. */}
      {patronaOpen ? <PatronaVenster onClose={() => setPatronaOpen(false)} /> : null}

      <style>{MEDIA_REGELS}</style>
    </div>
  );
}
